// ✅ src/pages/Asset/AssetReceiveListing.jsx  (FULL FILE - UPDATED WITH localStorage + commonHeaders)

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaEye, FaChevronDown, FaChevronUp } from "react-icons/fa";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/transferListing";
const RECEIVING_ROUTE = "/Asset/AssetTransferReceiving";

// dd/MM/yyyy helper
const formatDMY = (dateObj) => {
  if (!dateObj) return "";
  const d = new Date(dateObj);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const AssetReceiveListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState({
    fromDate: "03/09/2025",
    toDate: "10/09/2025",
    fromLocation: "",
    toLocation: "",
  });

  const [branches, setBranches] = useState([]);
  const [loadingInit, setLoadingInit] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  const [rights, setRights] = useState({ canAdd: false, canView: true });
  const [records, setRecords] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  const totalPages = Math.ceil(records.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentRecords = records.slice(startIndex, endIndex);

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // read branch id/name from SP output safely
  const getBranchId = (row) =>
    row?.fld_branchid ?? row?.branchid ?? row?.BranchId ?? "";

  const getBranchName = (row) =>
    row?.fld_branchName ??
    row?.fld_BranchName ??
    row?.branchName ??
    row?.BranchName ??
    "";

  // map different SP column names safely
  const getCol = (row, keys, fallback = "") => {
    for (const k of keys) {
      const v = row?.[k];
      if (v !== undefined && v !== null && String(v).trim() !== "") return v;
    }
    return fallback;
  };

  // ✅ TransferId extractor
  const getTransferId = (row) =>
    getCol(
      row,
      ["fld_Asset_Transferid", "TransferId", "fld_transferId", "transferId", "Id"],
      ""
    );

  // ✅ Redirect to receiving page
  const goToReceiving = (row, mode = "RECEIVE") => {
    const transferId = getTransferId(row);

    if (!transferId) {
      toast.error("Transfer Id not found in this row");
      console.log("Row:", row);
      return;
    }

    sessionStorage.setItem("assetTransferMode", mode);
    sessionStorage.setItem(
      "assetTransferSelected",
      JSON.stringify({ transferId })
    );

    navigate(
      `${RECEIVING_ROUTE}?transferId=${encodeURIComponent(
        transferId
      )}&mode=${encodeURIComponent(mode)}`,
      {
        state: {
          transferId: String(transferId),
          raw: row,
          mode,
        },
      }
    );
  };

  const handleViewRow = (row) => goToReceiving(row, "RECEIVE");
  const handleReceive = (row) => goToReceiving(row, "RECEIVE");

  // ✅ INIT: branches + default dates
  const loadInit = async () => {
    try {
      setLoadingInit(true);

      const res = await axios.get(`${API_BASE}/init`, {
        params: {
          loginuserid: userId,
          userId,
          branchid: branchId,
          branchtype: branchType,
          finYearId,
          finYear,
        },
        headers: commonHeaders,
      });

      const data = res?.data?.data || {};
      const initBranches = data?.branches || [];

      setBranches(Array.isArray(initBranches) ? initBranches : []);

      const initFrom = data?.from ? formatDMY(data.from) : "";
      const initTo = data?.defaultToDate ? formatDMY(data.defaultToDate) : "";

      setFilters((prev) => ({
        ...prev,
        fromDate: initFrom || prev.fromDate,
        toDate: initTo || prev.toDate,
      }));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load init data.");
      setBranches([]);
    } finally {
      setLoadingInit(false);
    }
  };

  // ✅ LIST: receive listing (pageid=2)
  const fetchList = async () => {
    try {
      setLoadingList(true);
      setCurrentPage(1);

      const params = {
        pageid: "2",
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        fromLocation: filters.fromLocation,
        toLocation: filters.toLocation,
        loginuserid: userId,
        userId,
        branchtype: branchType,
        branchid: branchId,
        finYearId,
        finYear,
      };

      const res = await axios.get(`${API_BASE}/list`, {
        params,
        headers: commonHeaders,
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Failed to load list");
        setRecords([]);
        return;
      }

      setRights(res?.data?.rights || { canAdd: false, canView: true });
      setRecords(Array.isArray(res?.data?.data) ? res.data.data : []);
      toast.success("List loaded");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load receive list");
      setRecords([]);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleViewClick = () => fetchList();
  const handlePageChange = (page) => setCurrentPage(page);

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 10;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      for (let i = 1; i <= maxVisiblePages; i++) pages.push(i);
      pages.push("...");
    }
    return pages;
  };

  const canShowReceive = useMemo(() => true, []);

  return (
    <AssetLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ASSET RECEIVE</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white text-black`
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Asset Receive Listing
              </h1>
            </div>
          </div>

          <div
            className={`mb-6 rounded-lg border ${
              isDark ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-gray-50"
            }`}
          >
            <div
              className={`flex justify-between items-center p-4 cursor-pointer ${
                isDark ? "hover:bg-gray-700/50" : "hover:bg-gray-100"
              }`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <h3 className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                Search Filters
              </h3>
              {showFilters ? <FaChevronUp /> : <FaChevronDown />}
            </div>

            {showFilters && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      From Date
                    </label>
                    <input
                      type="text"
                      value={filters.fromDate}
                      onChange={(e) => handleFilterChange("fromDate", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                      placeholder="dd/mm/yyyy"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      To Date
                    </label>
                    <input
                      type="text"
                      value={filters.toDate}
                      onChange={(e) => handleFilterChange("toDate", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                      placeholder="dd/mm/yyyy"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      From Location
                    </label>
                    <select
                      value={filters.fromLocation}
                      onChange={(e) => handleFilterChange("fromLocation", e.target.value)}
                      disabled={loadingInit}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      } ${loadingInit ? "opacity-70 cursor-not-allowed" : ""}`}
                    >
                      <option value="">{loadingInit ? "Loading..." : "Select"}</option>
                      {branches.map((b, idx) => (
                        <option key={`${getBranchId(b)}-${idx}`} value={getBranchId(b)}>
                          {getBranchName(b)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      To Location
                    </label>
                    <select
                      value={filters.toLocation}
                      onChange={(e) => handleFilterChange("toLocation", e.target.value)}
                      disabled={loadingInit}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      } ${loadingInit ? "opacity-70 cursor-not-allowed" : ""}`}
                    >
                      <option value="">{loadingInit ? "Loading..." : "Select"}</option>
                      {branches.map((b, idx) => (
                        <option key={`${getBranchId(b)}-${idx}`} value={getBranchId(b)}>
                          {getBranchName(b)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex justify-center">
                  <button
                    onClick={handleViewClick}
                    disabled={loadingList}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                      loadingList ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaEye />
                    {loadingList ? "Loading..." : "View"}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            {records.length === 0 ? (
              <div className={`p-12 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-xl font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  No Records Found
                </div>
                <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  No asset receive records match the current filter criteria.
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold border-r border-white/20">S.No</th>
                      <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Transfer Date</th>
                      <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Asset Type</th>
                      <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Asset ID/Code</th>
                      <th className="px-4 py-4 text-center font-semibold border-r border-white/20">From Location</th>
                      <th className="px-4 py-4 text-center font-semibold border-r border-white/20">To Location</th>
                      <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Status</th>
                      <th className="px-4 py-4 text-center font-semibold">Actions</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {currentRecords.map((r, idx) => {
                      const sno = startIndex + idx + 1;

                      const transferDate = getCol(r, ["TransferDate", "fld_transferDate", "Date", "fld_date"]);
                      const assetType = getCol(r, ["AssetType", "fld_assetType", "AssetTypeName"]);
                      const assetCode = getCol(r, ["AssetCode", "fld_assetCode", "idCode", "AssetIdCode", "fld_idcodeno"]);
                      const fromLoc = getCol(r, ["FromLocation", "FromBranch", "fld_fromBranch"]);
                      const toLoc = getCol(r, ["ToLocation", "ToBranch", "fld_toBranch"]);
                      const status = getCol(r, ["Status", "fld_status", "TransferStatus"], "");

                      const flags = r?._flags || {};
                      const uiMode = r?._uiMode || {};
                      const allowReceive =
                        (uiMode.showReceive ?? canShowReceive) &&
                        (flags.canReceive ?? true);

                      return (
                        <tr
                          key={r?.id ?? r?.TransferId ?? r?.fld_Asset_Transferid ?? `${sno}`}
                          className={`transition-colors duration-200 ${
                            idx % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-gray-50"
                              : isDark
                              ? "bg-gray-900"
                              : "bg-white"
                          }`}
                        >
                          <td className={`px-4 py-3 text-center ${isDark ? "text-gray-200" : "text-gray-800"}`}>{sno}</td>
                          <td className={`px-4 py-3 text-center ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                            {String(transferDate || "")}
                          </td>
                          <td className={`px-4 py-3 text-center ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                            {String(assetType || "")}
                          </td>
                          <td className={`px-4 py-3 text-center ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                            {String(assetCode || "")}
                          </td>
                          <td className={`px-4 py-3 text-center ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                            {String(fromLoc || "")}
                          </td>
                          <td className={`px-4 py-3 text-center ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                            {String(toLoc || "")}
                          </td>
                          <td className={`px-4 py-3 text-center ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                            {String(status || "")}
                          </td>

                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => handleViewRow(r)}
                                className={`px-3 py-1 rounded-lg text-sm font-semibold border ${
                                  isDark
                                    ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                                }`}
                                title="View"
                              >
                                View
                              </button>

                              {allowReceive && (
                                <button
                                  onClick={() => handleReceive(r)}
                                  className={`px-3 py-1 rounded-lg text-sm font-semibold ${buttonClass} ${hoverClass} text-white ${
                                    flags.canReceive === false ? "opacity-60 cursor-not-allowed" : ""
                                  }`}
                                  disabled={flags.canReceive === false}
                                  title="Receive"
                                >
                                  Receive
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {records.length > 0 && (
            <div className="flex justify-start mt-6">
              <div className="flex gap-1">
                {renderPageNumbers().map((page, index) => (
                  <button
                    key={index}
                    onClick={() => page !== "..." && handlePageChange(page)}
                    disabled={page === "..."}
                    className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                      page === currentPage
                        ? "bg-blue-600 text-white border-blue-600"
                        : page === "..."
                        ? isDark
                          ? "bg-gray-800 text-gray-400 border-gray-700 cursor-not-allowed"
                          : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                        : isDark
                        ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AssetLayoutWrapper>
  );
};

export default AssetReceiveListing;